import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, Brain, LayoutGrid, List, Search, Folder } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { AIToolList } from "@/components/AIToolCard";
import { AgentBuilderModal } from "@/components/AgentBuilderModal";
import { ShareAgentDialog } from "@/components/ShareAgentDialog";
import { ShareAgentFolderDialog } from "@/components/ShareAgentFolderDialog";
import { AgentFoldersBar } from "@/components/AgentFoldersBar";
import { agentFoldersService, type AgentFolder, type AgentFolderTreeNode } from "@/services/agentFoldersService";
import { aiAgentsService, agentToInsertPayload } from "@/services/aiAgentsService";
import { AGENTS } from "@/data/aiToolkit";
import type { Agent, AITool } from "@/types/aiToolkit";
import EmptyState from "@/components/EmptyState";

type ViewMode = "grid" | "list";

export function AgentsHubPanel() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [search, setSearch] = useState("");
  const [showBuiltInAgents, setShowBuiltInAgents] = useState(true);
  const [selectedAgentFolderId, setSelectedAgentFolderId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("folder");
  });

  const [agentBuilderOpen, setAgentBuilderOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [shareAgentDialogOpen, setShareAgentDialogOpen] = useState(false);
  const [agentToShare, setAgentToShare] = useState<Agent | null>(null);
  const [shareAgentFolderDialogOpen, setShareAgentFolderDialogOpen] = useState(false);
  const [agentFolderToShare, setAgentFolderToShare] = useState<AgentFolder | null>(null);
  const [newAgentFolderDialogOpen, setNewAgentFolderDialogOpen] = useState(false);
  const [newAgentFolderName, setNewAgentFolderName] = useState("");
  const [newAgentFolderParentId, setNewAgentFolderParentId] = useState<string | null>(null);
  const [renameAgentFolderDialogOpen, setRenameAgentFolderDialogOpen] = useState(false);
  const [renamingAgentFolder, setRenamingAgentFolder] = useState<AgentFolder | null>(null);
  const [renameAgentFolderName, setRenameAgentFolderName] = useState("");
  const [moveAgentToFolderDialogOpen, setMoveAgentToFolderDialogOpen] = useState(false);
  const [agentToMoveToFolder, setAgentToMoveToFolder] = useState<Agent | null>(null);
  const [agentsError, setAgentsError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const folder = params.get("folder");
    setSelectedAgentFolderId(folder);
  }, [typeof window !== "undefined" ? window.location.search : ""]);

  const { data: userAgents = [], isLoading: isLoadingUserAgents, error: agentsErrorQuery } = useQuery<Agent[]>({
    queryKey: ["/api/ai-agents", user?.id],
    queryFn: async () => {
      if (!user) return [];
      return aiAgentsService.getAgents();
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (agentsErrorQuery) {
      setAgentsError(agentsErrorQuery instanceof Error ? agentsErrorQuery.message : "Failed to load agents");
    }
  }, [agentsErrorQuery]);

  const { data: agentFolderTree = [], refetch: refetchAgentFolders } = useQuery<AgentFolderTreeNode[]>({
    queryKey: ["/api/agent-folders", user?.id],
    queryFn: async () => {
      if (!user) return [];
      return agentFoldersService.getFolderTree();
    },
    enabled: !!user,
  });

  const agentFoldersFlat = useMemo(() => {
    const flat: AgentFolder[] = [];
    const walk = (nodes: AgentFolderTreeNode[]) => {
      for (const n of nodes) {
        flat.push(n);
        if (n.children.length) walk(n.children);
      }
    };
    walk(agentFolderTree);
    return flat;
  }, [agentFolderTree]);

  const agentCountByFolderId = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const agent of userAgents) {
      if (agent.folderId) counts[agent.folderId] = (counts[agent.folderId] || 0) + 1;
    }
    return counts;
  }, [userAgents]);

  const agentFolderTreeWithCounts = useMemo(() => {
    const applyCounts = (nodes: AgentFolderTreeNode[]): AgentFolderTreeNode[] =>
      nodes.map((n) => ({
        ...n,
        agentCount: agentCountByFolderId[n.id] ?? 0,
        children: applyCounts(n.children),
      }));
    return applyCounts(agentFolderTree);
  }, [agentFolderTree, agentCountByFolderId]);

  const createAgentFolderMutation = useMutation({
    mutationFn: async ({ name, parentId }: { name: string; parentId?: string | null }) =>
      agentFoldersService.createFolder({ name, parentId: parentId ?? null }),
    onSuccess: () => {
      refetchAgentFolders();
      queryClient.invalidateQueries({ queryKey: ["/api/agent-folders"] });
      setNewAgentFolderDialogOpen(false);
      setNewAgentFolderName("");
      setNewAgentFolderParentId(null);
      toast({ title: "Folder created" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create folder", description: error.message, variant: "destructive" });
    },
  });

  const renameAgentFolderMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      agentFoldersService.updateFolder(id, { name }),
    onSuccess: () => {
      refetchAgentFolders();
      setRenameAgentFolderDialogOpen(false);
      setRenamingAgentFolder(null);
      toast({ title: "Folder renamed" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to rename folder", description: error.message, variant: "destructive" });
    },
  });

  const deleteAgentFolderMutation = useMutation({
    mutationFn: async (id: string) => agentFoldersService.deleteFolder(id),
    onSuccess: () => {
      refetchAgentFolders();
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agents"] });
      if (selectedAgentFolderId) handleSelectAgentFolder(null);
      toast({ title: "Folder deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete folder", description: error.message, variant: "destructive" });
    },
  });

  const moveAgentToFolderMutation = useMutation({
    mutationFn: async ({ agentId, folderId }: { agentId: string; folderId: string | null }) =>
      agentFoldersService.moveAgentToFolder(agentId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ai-agents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agent-folders"] });
      refetchAgentFolders();
      setMoveAgentToFolderDialogOpen(false);
      setAgentToMoveToFolder(null);
      toast({ title: "Agent moved" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to move agent", description: error.message, variant: "destructive" });
    },
  });

  const handleSelectAgentFolder = (folderId: string | null) => {
    setSelectedAgentFolderId(folderId);
    const params = new URLSearchParams(window.location.search);
    if (folderId) params.set("folder", folderId);
    else params.delete("folder");
    const qs = params.toString();
    setLocation(qs ? `/ai-agents?${qs}` : "/ai-agents");
  };

  const agentViewTools = useMemo(() => {
    const builtIn = AGENTS as AITool[];
    const userCustom = userAgents;
    let folderFilteredUser =
      selectedAgentFolderId === null
        ? userCustom.filter((a) => !a.folderId)
        : userCustom.filter((a) => a.folderId === selectedAgentFolderId);

    if (search.trim()) {
      const q = search.toLowerCase();
      const match = (t: AITool) =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q));
      folderFilteredUser = folderFilteredUser.filter(match);
      const filteredBuiltIn = showBuiltInAgents && !selectedAgentFolderId ? builtIn.filter(match) : [];
      return [...filteredBuiltIn, ...folderFilteredUser];
    }

    if (selectedAgentFolderId) return folderFilteredUser;
    const showBuiltIn = showBuiltInAgents ? builtIn : [];
    return [...showBuiltIn, ...folderFilteredUser];
  }, [userAgents, selectedAgentFolderId, showBuiltInAgents, search]);

  const handleAIToolClick = (tool: AITool) => {
    if (tool.tier !== "agent") return;
    const isUserCreated = tool.createdBy === "user" || userAgents.some((a) => a.id === tool.id);
    if (isUserCreated && tool.createdBy === "user") {
      setLocation(`/ai-agents/${tool.id}/edit`);
      return;
    }
    setEditingAgent(tool as Agent);
    setAgentBuilderOpen(true);
  };

  const handleShareAgent = (tool: AITool) => {
    if (tool.tier !== "agent" || tool.createdBy !== "user") return;
    setAgentToShare(tool as Agent);
    setShareAgentDialogOpen(true);
  };

  const handleMoveAgentToFolder = (tool: AITool) => {
    if (tool.tier !== "agent" || tool.createdBy !== "user") return;
    setAgentToMoveToFolder(tool as Agent);
    setMoveAgentToFolderDialogOpen(true);
  };

  const handleShareAgentFolder = (folder: AgentFolder) => {
    const count = userAgents.filter((a) => a.folderId === folder.id).length;
    if (count === 0) {
      toast({
        title: "Empty folder",
        description: "Add at least one agent before sharing this folder.",
        variant: "destructive",
      });
      return;
    }
    setAgentFolderToShare(folder);
    setShareAgentFolderDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search agents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border rounded-md">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button
            onClick={() => {
              setEditingAgent(null);
              setAgentBuilderOpen(true);
            }}
            data-testid="button-create-agent"
          >
            <Plus className="h-4 w-4 mr-1" />
            Create Agent
          </Button>
        </div>
      </div>

      {!selectedAgentFolderId && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBuiltInAgents(!showBuiltInAgents)}
          >
            {showBuiltInAgents ? "Hide" : "Show"} Built-in Agents
          </Button>
          <span className="text-sm text-muted-foreground">
            {agentViewTools.length} agent{agentViewTools.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {isLoadingUserAgents && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {agentsError && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-sm text-destructive">{agentsError}</p>
        </div>
      )}

      <AgentFoldersBar
        agentFolderTree={agentFolderTreeWithCounts}
        selectedAgentFolderId={selectedAgentFolderId}
        onSelectFolder={handleSelectAgentFolder}
        onCreateFolder={() => {
          setNewAgentFolderParentId(null);
          setNewAgentFolderDialogOpen(true);
        }}
        onCreateSubfolder={(parentId) => {
          setNewAgentFolderParentId(parentId);
          setNewAgentFolderDialogOpen(true);
        }}
        onRenameFolder={(folder) => {
          setRenamingAgentFolder(folder);
          setRenameAgentFolderName(folder.name);
          setRenameAgentFolderDialogOpen(true);
        }}
        onShareFolder={handleShareAgentFolder}
        onDeleteFolder={(id) => deleteAgentFolderMutation.mutate(id)}
      />

      <AIToolList
        tools={agentViewTools}
        onToolClick={handleAIToolClick}
        onShareAgent={handleShareAgent}
        onMoveAgentToFolder={handleMoveAgentToFolder}
        viewMode={viewMode}
        agentFolderNames={Object.fromEntries(agentFoldersFlat.map((f) => [f.id, f.name]))}
      />

      {agentViewTools.length === 0 && !isLoadingUserAgents && (
        <EmptyState
          icon={Brain}
          title="No agents found"
          description="Create an agent or adjust your search"
        />
      )}

      {agentToShare && (
        <ShareAgentDialog
          open={shareAgentDialogOpen}
          onOpenChange={(open) => {
            setShareAgentDialogOpen(open);
            if (!open) setAgentToShare(null);
          }}
          agentId={agentToShare.id}
          agentName={agentToShare.name}
        />
      )}

      {agentFolderToShare && (
        <ShareAgentFolderDialog
          open={shareAgentFolderDialogOpen}
          onOpenChange={(open) => {
            setShareAgentFolderDialogOpen(open);
            if (!open) setAgentFolderToShare(null);
          }}
          folderId={agentFolderToShare.id}
          folderName={agentFolderToShare.name}
          agentCount={userAgents.filter((a) => a.folderId === agentFolderToShare.id).length}
        />
      )}

      <Dialog open={newAgentFolderDialogOpen} onOpenChange={setNewAgentFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Agent Folder</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Folder name"
            value={newAgentFolderName}
            onChange={(e) => setNewAgentFolderName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewAgentFolderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (newAgentFolderName.trim()) {
                  createAgentFolderMutation.mutate({
                    name: newAgentFolderName.trim(),
                    parentId: newAgentFolderParentId,
                  });
                }
              }}
              disabled={!newAgentFolderName.trim() || createAgentFolderMutation.isPending}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameAgentFolderDialogOpen} onOpenChange={setRenameAgentFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
          </DialogHeader>
          <Input
            value={renameAgentFolderName}
            onChange={(e) => setRenameAgentFolderName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameAgentFolderDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (renamingAgentFolder && renameAgentFolderName.trim()) {
                  renameAgentFolderMutation.mutate({
                    id: renamingAgentFolder.id,
                    name: renameAgentFolderName.trim(),
                  });
                }
              }}
              disabled={!renameAgentFolderName.trim() || renameAgentFolderMutation.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={moveAgentToFolderDialogOpen} onOpenChange={setMoveAgentToFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            <Button
              variant={agentToMoveToFolder && !agentToMoveToFolder.folderId ? "secondary" : "outline"}
              className="w-full justify-start"
              onClick={() => {
                if (agentToMoveToFolder) {
                  moveAgentToFolderMutation.mutate({ agentId: agentToMoveToFolder.id, folderId: null });
                }
              }}
            >
              No folder (root)
            </Button>
            {agentFoldersFlat.map((folder) => (
              <Button
                key={folder.id}
                variant={agentToMoveToFolder?.folderId === folder.id ? "secondary" : "outline"}
                className="w-full justify-start gap-2"
                onClick={() => {
                  if (agentToMoveToFolder) {
                    moveAgentToFolderMutation.mutate({
                      agentId: agentToMoveToFolder.id,
                      folderId: folder.id,
                    });
                  }
                }}
              >
                <Folder className="h-4 w-4" />
                {folder.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AgentBuilderModal
        open={agentBuilderOpen}
        onOpenChange={setAgentBuilderOpen}
        agent={editingAgent}
        defaultFolderId={selectedAgentFolderId}
        onShare={(a) => {
          setAgentToShare(a);
          setShareAgentDialogOpen(true);
        }}
        onSave={async (agent) => {
          try {
            if (editingAgent && editingAgent.createdBy === "user") {
              await aiAgentsService.updateAgent(editingAgent.id, agentToInsertPayload(agent));
              toast({ title: "Agent updated" });
            } else {
              const created = await aiAgentsService.createAgent({
                ...agentToInsertPayload(agent),
                folderId: agent.folderId ?? selectedAgentFolderId ?? null,
              });
              if (created) toast({ title: "Agent created" });
            }
            queryClient.invalidateQueries({ queryKey: ["/api/ai-agents"] });
            setAgentBuilderOpen(false);
            setEditingAgent(null);
          } catch (e) {
            toast({
              title: "Failed to save agent",
              description: e instanceof Error ? e.message : "Unknown error",
              variant: "destructive",
            });
          }
        }}
        onDelete={async (agentId) => {
          await aiAgentsService.deleteAgent(agentId);
          queryClient.invalidateQueries({ queryKey: ["/api/ai-agents"] });
          toast({ title: "Agent deleted" });
        }}
      />
    </div>
  );
}
